import React, { useEffect, useState, useCallback } from "react";
import {
  Card, CardBody, Col, Container, Row,
  Badge, Input, Label, Spinner, Modal,
  ModalHeader, ModalBody, ModalFooter, Alert, FormGroup,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";

const BASE_URL = "https://hiflowapi.stechomeyazilim.info";

const EMPTY_FORM = {
  Advert_Pricing_Title: "",
  Advert_Pricing_Desc: "",
  Price: "",
  Is_Cancel_Policy: false,
  Cancel_Days_Limit: "",
};

const FiyatYonetimi = () => {
  document.title = "Fiyat & İptal Politikası Yönetimi | LojGo";

  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteId,    setDeleteId]    = useState(null);
  const [alert,       setAlert]       = useState(null);

  // Modal state'leri
  const [modal,       setModal]       = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editRow,     setEditRow]     = useState(null); // null = yeni kayıt
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [formErrors,  setFormErrors]  = useState({});

  // Aktif sekme: "all" | "service" | "cancel"
  const [tab, setTab] = useState("all");

  // ─── SİSTEM AYARLARI (Settings) ───────────────────────────────
  const [settingsId,           setSettingsId]           = useState(null);
  const [quickOfferPct,        setQuickOfferPct]        = useState("");
  const [settingsLoading,      setSettingsLoading]      = useState(true);
  const [settingsSaveLoading,  setSettingsSaveLoading]  = useState(false);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/Settings?$top=1`);
      const data = await res.json();
      const row  = data?.value?.[0];
      if (row) {
        setSettingsId(row.ID);
        setQuickOfferPct(row.Quick_Offer_Percentage ?? "");
      }
    } catch {
      showAlert("danger", "Sistem ayarları yüklenemedi.");
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSaveSettings = async () => {
    const pct = parseFloat(quickOfferPct);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      showAlert("danger", "Yüzde değeri 0-100 arasında olmalıdır.");
      return;
    }
    setSettingsSaveLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/Settings(${settingsId})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Quick_Offer_Percentage: pct }),
      });
      if (!res.ok) throw new Error();
      showAlert("success", "Sistem ayarları başarıyla kaydedildi.");
    } catch {
      showAlert("danger", "Ayarlar kaydedilemedi.");
    } finally {
      setSettingsSaveLoading(false);
    }
  };

  // ─── VERİ ÇEKME ───────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/Advert_Pricing?$orderby=Is_Cancel_Policy desc,Cancel_Days_Limit asc,ID asc`);
      const data = await res.json();
      setRows(data.value || []);
    } catch {
      showAlert("danger", "Veriler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── FİLTRELENMİŞ LİSTE ───────────────────────────────────────
  const filtered = rows.filter(r => {
    if (tab === "cancel")  return !!r.Is_Cancel_Policy;
    if (tab === "service") return !r.Is_Cancel_Policy;
    return true;
  });

  const cancelRows  = rows.filter(r =>  r.Is_Cancel_Policy);
  const serviceRows = rows.filter(r => !r.Is_Cancel_Policy);

  // ─── MODAL AÇMA ───────────────────────────────────────────────
  const openNew = (isCancel = false) => {
    setEditRow(null);
    setForm({ ...EMPTY_FORM, Is_Cancel_Policy: isCancel });
    setFormErrors({});
    setModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      Advert_Pricing_Title: row.Advert_Pricing_Title || "",
      Advert_Pricing_Desc:  row.Advert_Pricing_Desc  || "",
      Price:                row.Price?.toString()     || "0",
      Is_Cancel_Policy:     !!row.Is_Cancel_Policy,
      Cancel_Days_Limit:    row.Cancel_Days_Limit?.toString() ?? "",
    });
    setFormErrors({});
    setModal(true);
  };

  // ─── FORM VALİDASYON ──────────────────────────────────────────
  const validate = () => {
    const errors = {};
    if (!form.Advert_Pricing_Title.trim()) errors.title = "Başlık zorunludur.";
    if (form.Price === "" || isNaN(Number(form.Price))) errors.price = "Geçerli bir fiyat giriniz.";
    if (Number(form.Price) < 0) errors.price = "Fiyat negatif olamaz.";
    if (form.Is_Cancel_Policy) {
      if (form.Cancel_Days_Limit === "" || isNaN(Number(form.Cancel_Days_Limit))) {
        errors.days = "Gün limiti zorunludur.";
      } else if (Number(form.Cancel_Days_Limit) < 0) {
        errors.days = "Gün limiti negatif olamaz.";
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ─── KAYDET (EKLE / GÜNCELLE) ─────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setSaveLoading(true);
    try {
      const body = {
        Advert_Pricing_Title: form.Advert_Pricing_Title.trim(),
        Advert_Pricing_Desc:  form.Advert_Pricing_Desc.trim(),
        Price:                Number(form.Price),
        Is_Cancel_Policy:     form.Is_Cancel_Policy,
        Cancel_Days_Limit:    form.Is_Cancel_Policy ? Number(form.Cancel_Days_Limit) : null,
      };

      if (editRow) {
        const res = await fetch(`${BASE_URL}/Advert_Pricing(${editRow.ID})`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
        showAlert("success", `"${body.Advert_Pricing_Title}" başarıyla güncellendi.`);
      } else {
        const res = await fetch(`${BASE_URL}/Advert_Pricing`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
        showAlert("success", `"${body.Advert_Pricing_Title}" başarıyla eklendi.`);
      }

      setModal(false);
      loadData();
    } catch {
      showAlert("danger", "Kayıt sırasında hata oluştu. Tekrar deneyin.");
    } finally {
      setSaveLoading(false);
    }
  };

  // ─── SİL ──────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteId) return;
    setSaveLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/Advert_Pricing(${deleteId})`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      showAlert("success", "Kayıt silindi.");
      setDeleteModal(false);
      setDeleteId(null);
      loadData();
    } catch {
      showAlert("danger", "Silme işlemi başarısız. Kayıt kullanımda olabilir.");
      setDeleteModal(false);
    } finally {
      setSaveLoading(false);
    }
  };

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: undefined }));
  };

  // ─── GÜN ETİKETİ ──────────────────────────────────────────────
  const dayLabel = (limit) => {
    if (limit === 0) return "Aynı gün";
    if (limit === 1) return "1 gün öncesi";
    return `${limit} gün öncesine kadar`;
  };

  // ─── TAB BUTONU ───────────────────────────────────────────────
  const TabBtn = ({ id, label, count, color }) => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding: "8px 20px", borderRadius: 10, fontWeight: 600, fontSize: 13,
        cursor: "pointer", transition: "all 0.2s",
        border: tab === id ? `2px solid ${color}` : "2px solid #e9ecef",
        background: tab === id ? color : "#fff",
        color: tab === id ? "#fff" : "#6c757d",
      }}
    >
      {label}
      {count > 0 && (
        <span style={{
          marginLeft: 6,
          background: tab === id ? "rgba(255,255,255,0.25)" : color,
          color: tab === id ? "#fff" : "#fff",
          borderRadius: 20, padding: "1px 7px", fontSize: 11,
        }}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Fiyat & İptal Politikası" />

          {alert && (
            <Alert color={alert.type} className="d-flex align-items-center gap-2">
              <i className={`bx ${alert.type === "success" ? "bx-check-circle" : "bx-error-circle"} fs-5`} />
              {alert.msg}
            </Alert>
          )}

          {/* ── Sistem Ayarları Kartı ───────────────────────── */}
          <Card style={{
            border: "none", borderRadius: 16, marginBottom: 24,
            boxShadow: "0 4px 20px rgba(241,90,41,0.12)",
            borderLeft: "4px solid #F15A29",
          }}>
            <CardBody>
              <div className="d-flex align-items-center gap-2 mb-3">
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: "linear-gradient(135deg,#F15A29,#fd7e14)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <i className="bx bx-cog text-white" style={{ fontSize: 20 }} />
                </div>
                <span style={{ fontWeight: 700, fontSize: 16, color: "#344050" }}>
                  Sistem Ayarları
                </span>
              </div>

              {settingsLoading ? (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner size="sm" /> Yükleniyor...
                </div>
              ) : (
                <Row className="align-items-end g-3">
                  <Col md={4}>
                    <Label style={{ fontWeight: 600, color: "#344050", fontSize: 13 }}>
                      Hızlı Teklif Yüzdesi (%)
                      <span style={{ color: "#6c757d", fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                        — İlan toplam tutarının yüzde kaçı hızlı teklif fiyatı olsun?
                      </span>
                    </Label>
                    <div className="input-group">
                      <span className="input-group-text" style={{
                        background: "linear-gradient(135deg,#F15A29,#fd7e14)",
                        border: "none", color: "#fff", borderRadius: "10px 0 0 10px",
                      }}>
                        <i className="bx bx-percent" style={{ fontSize: 17 }} />
                      </span>
                      <Input
                        type="number" min="0" max="100" step="0.01"
                        placeholder="Örn: 50"
                        value={quickOfferPct}
                        onChange={e => setQuickOfferPct(e.target.value)}
                        style={{ borderLeft: "none", borderRadius: "0 10px 10px 0", maxWidth: 180 }}
                      />
                    </div>
                    {quickOfferPct !== "" && !isNaN(parseFloat(quickOfferPct)) && (
                      <div style={{ fontSize: 12, color: "#6c757d", marginTop: 4 }}>
                        <i className="bx bx-info-circle me-1" />
                        Örn: 22.000 TL ilanın hızlı teklifi →{" "}
                        <strong style={{ color: "#F15A29" }}>
                          {(22000 * parseFloat(quickOfferPct) / 100).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} TL
                        </strong>
                      </div>
                    )}
                  </Col>
                  <Col md="auto">
                    <button
                      onClick={handleSaveSettings}
                      disabled={settingsSaveLoading}
                      style={{
                        padding: "9px 22px", borderRadius: 10, border: "none",
                        background: "linear-gradient(135deg,#F15A29,#fd7e14)",
                        color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 6,
                        boxShadow: "0 4px 12px rgba(241,90,41,0.3)",
                        opacity: settingsSaveLoading ? 0.8 : 1,
                      }}
                    >
                      {settingsSaveLoading
                        ? <><Spinner size="sm" />Kaydediliyor...</>
                        : <><i className="bx bx-save" />Kaydet</>}
                    </button>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>

          {/* ── İstatistik Kartları ─────────────────────────── */}
          <Row className="mb-4">
            {[
              {
                label: "Toplam Kayıt", value: rows.length,
                icon: "bx-list-ul", grad: "linear-gradient(135deg,#556ee6,#6f42c1)",
                shadow: "rgba(85,110,230,0.2)",
              },
              {
                label: "Ekstra Hizmet", value: serviceRows.length,
                icon: "bx-package", grad: "linear-gradient(135deg,#0dcaf0,#0d6efd)",
                shadow: "rgba(13,202,240,0.2)",
              },
              {
                label: "İptal Politikası", value: cancelRows.length,
                icon: "bx-x-circle", grad: "linear-gradient(135deg,#dc3545,#ff6b6b)",
                shadow: "rgba(220,53,69,0.2)",
              },
              {
                label: "Maks. İptal Cezası",
                value: cancelRows.length > 0
                  ? Math.max(...cancelRows.map(r => r.Price || 0)).toLocaleString("tr-TR") + " TL"
                  : "—",
                icon: "bx-money", grad: "linear-gradient(135deg,#fd7e14,#ffc107)",
                shadow: "rgba(253,126,20,0.2)",
              },
            ].map((s, i) => (
              <Col md={3} key={i}>
                <Card style={{ border: "none", borderRadius: 16, boxShadow: `0 4px 20px ${s.shadow}` }}>
                  <CardBody className="d-flex align-items-center gap-3">
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, background: s.grad, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <i className={`bx ${s.icon} text-white`} style={{ fontSize: 24 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#344050" }}>{s.value}</div>
                      <div style={{ fontSize: 12, color: "#6c757d" }}>{s.label}</div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ── İptal Politikaları Özet Kartı ───────────────── */}
          {cancelRows.length > 0 && (
            <Card style={{ border: "none", borderRadius: 16, marginBottom: 24,
              boxShadow: "0 4px 20px rgba(220,53,69,0.10)",
              borderLeft: "4px solid #dc3545" }}>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bx bx-shield-x text-danger" style={{ fontSize: 22 }} />
                    <span style={{ fontWeight: 700, fontSize: 16, color: "#344050" }}>
                      Aktif İptal Cezası Politikası
                    </span>
                  </div>
                  <button
                    onClick={() => openNew(true)}
                    style={{
                      padding: "6px 14px", borderRadius: 8, border: "none",
                      background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
                      color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer",
                    }}
                  >
                    <i className="bx bx-plus me-1" />Kural Ekle
                  </button>
                </div>

                <div className="d-flex flex-wrap gap-3">
                  {[...cancelRows]
                    .sort((a, b) => (a.Cancel_Days_Limit ?? 999) - (b.Cancel_Days_Limit ?? 999))
                    .map((r, i) => (
                    <div key={r.ID} style={{
                      background: "#fff5f5", border: "2px solid #ffcdd2",
                      borderRadius: 14, padding: "14px 20px", minWidth: 200,
                      position: "relative",
                    }}>
                      {/* sıra numarası */}
                      <div style={{
                        position: "absolute", top: -10, left: 16,
                        background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
                        color: "#fff", borderRadius: 20, padding: "1px 10px",
                        fontSize: 11, fontWeight: 700,
                      }}>
                        Kural {i + 1}
                      </div>
                      <div style={{ fontSize: 13, color: "#6c757d", marginBottom: 4 }}>
                        <i className="bx bx-calendar me-1" />
                        {r.Cancel_Days_Limit !== null && r.Cancel_Days_Limit !== undefined
                          ? dayLabel(r.Cancel_Days_Limit)
                          : "—"}
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: "#dc3545" }}>
                        {(r.Price || 0).toLocaleString("tr-TR")} TL
                      </div>
                      <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 2 }}>
                        {r.Advert_Pricing_Title}
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button onClick={() => openEdit(r)} style={{
                          padding: "3px 10px", borderRadius: 6, border: "none",
                          background: "#fff3cd", color: "#856404",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                        }}>
                          <i className="bx bx-edit me-1" />Düzenle
                        </button>
                        <button onClick={() => { setDeleteId(r.ID); setDeleteModal(true); }} style={{
                          padding: "3px 10px", borderRadius: 6, border: "none",
                          background: "#ffcdd2", color: "#c0392b",
                          fontSize: 12, fontWeight: 600, cursor: "pointer",
                        }}>
                          <i className="bx bx-trash me-1" />Sil
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Ücretsiz iptal kuralı (otomatik) */}
                  <div style={{
                    background: "#f0fff4", border: "2px dashed #86efac",
                    borderRadius: 14, padding: "14px 20px", minWidth: 200,
                    display: "flex", flexDirection: "column", justifyContent: "center",
                  }}>
                    <div style={{ fontSize: 13, color: "#6c757d", marginBottom: 4 }}>
                      <i className="bx bx-check-circle me-1 text-success" />
                      {cancelRows.length > 0
                        ? `${Math.max(...cancelRows.map(r => r.Cancel_Days_Limit ?? 0)) + 1}+ gün öncesi`
                        : "Her zaman"}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "#198754" }}>
                      Ücretsiz
                    </div>
                    <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 2 }}>
                      Otomatik (kural gerekmez)
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* ── Ana Tablo Kartı ──────────────────────────────── */}
          <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <div style={{ height: 4, borderRadius: "16px 16px 0 0",
              background: "linear-gradient(90deg,#556ee6,#6f42c1,#e83e8c)" }} />
            <CardBody>

              {/* Toolbar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <div className="d-flex gap-2 flex-wrap">
                  <TabBtn id="all"     label="Tümü"           count={rows.length}        color="#556ee6" />
                  <TabBtn id="service" label="Ekstra Hizmetler" count={serviceRows.length} color="#0dcaf0" />
                  <TabBtn id="cancel"  label="İptal Politikaları" count={cancelRows.length}  color="#dc3545" />
                </div>
                <div className="d-flex gap-2">
                  <button
                    onClick={() => openNew(false)}
                    style={{
                      padding: "8px 16px", borderRadius: 10, border: "none",
                      background: "linear-gradient(135deg,#556ee6,#6f42c1)",
                      color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 6,
                      boxShadow: "0 4px 12px rgba(85,110,230,0.35)",
                    }}
                  >
                    <i className="bx bx-plus" style={{ fontSize: 18 }} />
                    Hizmet Ekle
                  </button>
                  <button
                    onClick={() => openNew(true)}
                    style={{
                      padding: "8px 16px", borderRadius: 10, border: "none",
                      background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
                      color: "#fff", fontWeight: 600, fontSize: 14, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 6,
                      boxShadow: "0 4px 12px rgba(220,53,69,0.3)",
                    }}
                  >
                    <i className="bx bx-plus" style={{ fontSize: 18 }} />
                    Ceza Kuralı Ekle
                  </button>
                  <button
                    onClick={loadData}
                    style={{
                      padding: "8px 14px", borderRadius: 10, cursor: "pointer",
                      background: "#f8f9ff", border: "1.5px solid #dee2e6",
                      color: "#556ee6", fontWeight: 600,
                      display: "flex", alignItems: "center", gap: 5,
                    }}
                  >
                    <i className="bx bx-refresh" style={{ fontSize: 17 }} />
                    Yenile
                  </button>
                </div>
              </div>

              {/* Tablo */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Yükleniyor...</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bx bx-folder-open" style={{ fontSize: 48, color: "#dee2e6" }} />
                  <p className="mt-2 text-muted">Kayıt bulunamadı.</p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-hover align-middle mb-0" style={{ fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: "#f8f9ff" }}>
                        {["#", "Tür", "Başlık", "Açıklama", "Fiyat", "Gün Limiti", "İşlem"].map(h => (
                          <th key={h} style={{
                            fontWeight: 700, color: "#344050",
                            padding: "12px 16px", border: "none", whiteSpace: "nowrap",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row, idx) => (
                        <tr key={row.ID} style={{ borderBottom: "1px solid #f1f3fa" }}>
                          <td style={{ padding: "12px 16px", color: "#adb5bd", fontWeight: 600 }}>
                            {row.ID}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            {row.Is_Cancel_Policy ? (
                              <Badge style={{
                                background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
                                padding: "5px 10px", borderRadius: 8, fontSize: 11,
                              }}>
                                <i className="bx bx-x-circle me-1" />İptal Cezası
                              </Badge>
                            ) : (
                              <Badge style={{
                                background: "linear-gradient(135deg,#0dcaf0,#0d6efd)",
                                padding: "5px 10px", borderRadius: 8, fontSize: 11,
                              }}>
                                <i className="bx bx-package me-1" />Ekstra Hizmet
                              </Badge>
                            )}
                          </td>
                          <td style={{ padding: "12px 16px", fontWeight: 700, color: "#344050" }}>
                            {row.Advert_Pricing_Title}
                          </td>
                          <td style={{ padding: "12px 16px", color: "#6c757d", maxWidth: 260 }}>
                            <div style={{
                              overflow: "hidden", textOverflow: "ellipsis",
                              whiteSpace: "nowrap", maxWidth: 240,
                            }}>
                              {row.Advert_Pricing_Desc || <span style={{ color: "#dee2e6" }}>—</span>}
                            </div>
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              fontWeight: 800, fontSize: 15,
                              color: row.Is_Cancel_Policy ? "#dc3545" : "#198754",
                            }}>
                              {(row.Price || 0).toLocaleString("tr-TR")} TL
                            </span>
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            {row.Is_Cancel_Policy && row.Cancel_Days_Limit !== null ? (
                              <span style={{
                                background: "#fff3cd", color: "#856404",
                                borderRadius: 8, padding: "4px 10px",
                                fontSize: 12, fontWeight: 600,
                              }}>
                                <i className="bx bx-calendar me-1" />
                                {dayLabel(row.Cancel_Days_Limit)}
                              </span>
                            ) : (
                              <span style={{ color: "#dee2e6" }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <div className="d-flex gap-2">
                              <button
                                onClick={() => openEdit(row)}
                                style={{
                                  padding: "6px 12px", borderRadius: 8, border: "none",
                                  background: "linear-gradient(135deg,#556ee6,#6f42c1)",
                                  color: "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer",
                                  display: "flex", alignItems: "center", gap: 4,
                                }}
                              >
                                <i className="bx bx-edit" />Düzenle
                              </button>
                              <button
                                onClick={() => { setDeleteId(row.ID); setDeleteModal(true); }}
                                style={{
                                  padding: "6px 12px", borderRadius: 8, border: "none",
                                  background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
                                  color: "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer",
                                  display: "flex", alignItems: "center", gap: 4,
                                }}
                              >
                                <i className="bx bx-trash" />Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && filtered.length > 0 && (
                <div className="text-muted mt-3" style={{ fontSize: 13 }}>
                  <strong>{filtered.length}</strong> kayıt gösteriliyor.
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* ════ EKLE / DÜZENLE MODALI ════════════════════════════ */}
      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="md">
        <ModalHeader
          toggle={() => setModal(false)}
          style={{ borderBottom: "none", paddingBottom: 0 }}
        >
          <div className="d-flex align-items-center gap-2">
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: form.Is_Cancel_Policy
                ? "linear-gradient(135deg,#dc3545,#ff6b6b)"
                : "linear-gradient(135deg,#556ee6,#6f42c1)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className={`bx ${form.Is_Cancel_Policy ? "bx-x-circle" : "bx-package"} text-white`}
                style={{ fontSize: 18 }} />
            </div>
            <span style={{ fontWeight: 700 }}>
              {editRow
                ? "Kaydı Düzenle"
                : form.Is_Cancel_Policy ? "Yeni İptal Cezası Kuralı" : "Yeni Ekstra Hizmet"}
            </span>
          </div>
        </ModalHeader>

        <ModalBody style={{ paddingTop: 8 }}>
          {/* Tür seçimi — sadece yeni kayıtta */}
          {!editRow && (
            <FormGroup className="mb-4">
              <Label style={{ fontWeight: 600, color: "#344050", fontSize: 13 }}>Kayıt Türü</Label>
              <div className="d-flex gap-3">
                {[
                  { val: false, label: "Ekstra Hizmet",    icon: "bx-package",  color: "#0d6efd" },
                  { val: true,  label: "İptal Cezası Kuralı", icon: "bx-x-circle", color: "#dc3545" },
                ].map(opt => (
                  <div
                    key={String(opt.val)}
                    onClick={() => handleChange("Is_Cancel_Policy", opt.val)}
                    style={{
                      flex: 1, border: `2px solid ${form.Is_Cancel_Policy === opt.val ? opt.color : "#e9ecef"}`,
                      borderRadius: 12, padding: "12px", textAlign: "center", cursor: "pointer",
                      background: form.Is_Cancel_Policy === opt.val ? `${opt.color}15` : "#fff",
                      transition: "all 0.15s",
                    }}
                  >
                    <i className={`bx ${opt.icon} d-block mb-1`}
                      style={{ fontSize: 22, color: form.Is_Cancel_Policy === opt.val ? opt.color : "#adb5bd" }} />
                    <span style={{
                      fontWeight: 700, fontSize: 13,
                      color: form.Is_Cancel_Policy === opt.val ? opt.color : "#6c757d",
                    }}>
                      {opt.label}
                    </span>
                  </div>
                ))}
              </div>
            </FormGroup>
          )}

          <FormGroup className="mb-3">
            <Label style={{ fontWeight: 600, color: "#344050", fontSize: 13 }}>
              Başlık <span style={{ color: "#dc3545" }}>*</span>
            </Label>
            <Input
              placeholder="Örn: Klima, Sigorta, İptal Cezası - Aynı Gün"
              value={form.Advert_Pricing_Title}
              onChange={e => handleChange("Advert_Pricing_Title", e.target.value)}
              invalid={!!formErrors.title}
              style={{ borderRadius: 10 }}
            />
            {formErrors.title && (
              <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>{formErrors.title}</div>
            )}
          </FormGroup>

          <FormGroup className="mb-3">
            <Label style={{ fontWeight: 600, color: "#344050", fontSize: 13 }}>Açıklama</Label>
            <Input
              type="textarea" rows={2}
              placeholder="Kısa bir açıklama (isteğe bağlı)"
              value={form.Advert_Pricing_Desc}
              onChange={e => handleChange("Advert_Pricing_Desc", e.target.value)}
              style={{ borderRadius: 10, resize: "none" }}
            />
          </FormGroup>

          <Row>
            <Col md={form.Is_Cancel_Policy ? 6 : 12}>
              <FormGroup className="mb-3">
                <Label style={{ fontWeight: 600, color: "#344050", fontSize: 13 }}>
                  {form.Is_Cancel_Policy ? "Ceza Tutarı (TL)" : "Fiyat (TL)"}
                  <span style={{ color: "#dc3545" }}> *</span>
                </Label>
                <div className="input-group">
                  <span className="input-group-text" style={{
                    background: form.Is_Cancel_Policy
                      ? "linear-gradient(135deg,#dc3545,#ff6b6b)"
                      : "linear-gradient(135deg,#556ee6,#6f42c1)",
                    border: "none", color: "#fff",
                    borderRadius: "10px 0 0 10px",
                  }}>
                    <i className="bx bx-lira" style={{ fontSize: 17 }} />
                  </span>
                  <Input
                    type="number" min="0" placeholder="0"
                    value={form.Price}
                    onChange={e => handleChange("Price", e.target.value)}
                    invalid={!!formErrors.price}
                    style={{ borderLeft: "none", borderRadius: "0 10px 10px 0" }}
                  />
                  {formErrors.price && (
                    <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4, width: "100%" }}>
                      {formErrors.price}
                    </div>
                  )}
                </div>
              </FormGroup>
            </Col>

            {form.Is_Cancel_Policy && (
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label style={{ fontWeight: 600, color: "#344050", fontSize: 13 }}>
                    Gün Limiti <span style={{ color: "#dc3545" }}>*</span>
                  </Label>
                  <div className="input-group">
                    <span className="input-group-text" style={{
                      background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
                      border: "none", color: "#fff",
                      borderRadius: "10px 0 0 10px",
                    }}>
                      <i className="bx bx-calendar" style={{ fontSize: 17 }} />
                    </span>
                    <Input
                      type="number" min="0" placeholder="Örn: 0, 3, 7"
                      value={form.Cancel_Days_Limit}
                      onChange={e => handleChange("Cancel_Days_Limit", e.target.value)}
                      invalid={!!formErrors.days}
                      style={{ borderLeft: "none", borderRadius: "0 10px 10px 0" }}
                    />
                    {formErrors.days && (
                      <div style={{ color: "#dc3545", fontSize: 12, marginTop: 4, width: "100%" }}>
                        {formErrors.days}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#6c757d", marginTop: 4 }}>
                    <i className="bx bx-info-circle me-1" />
                    0 = aynı gün, 3 = 3 güne kadar, vb.
                  </div>
                </FormGroup>
              </Col>
            )}
          </Row>

          {form.Is_Cancel_Policy && form.Cancel_Days_Limit !== "" && !isNaN(Number(form.Cancel_Days_Limit)) && (
            <div style={{
              background: "#fff5f5", border: "1.5px solid #ffcdd2",
              borderRadius: 10, padding: "10px 14px", marginTop: 4,
            }}>
              <i className="bx bx-info-circle text-danger me-2" />
              <span style={{ fontSize: 13, color: "#c0392b" }}>
                <strong>{dayLabel(Number(form.Cancel_Days_Limit))}</strong> iptal edilirse{" "}
                <strong>{Number(form.Price || 0).toLocaleString("tr-TR")} TL</strong> ceza uygulanır.
              </span>
            </div>
          )}
        </ModalBody>

        <ModalFooter style={{ borderTop: "none" }}>
          <button
            onClick={() => setModal(false)}
            style={{ padding: "8px 20px", borderRadius: 8, border: "1.5px solid #dee2e6",
              background: "#fff", color: "#6c757d", fontWeight: 600, cursor: "pointer" }}
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={saveLoading}
            style={{
              padding: "8px 24px", borderRadius: 8, border: "none",
              background: form.Is_Cancel_Policy
                ? "linear-gradient(135deg,#dc3545,#ff6b6b)"
                : "linear-gradient(135deg,#556ee6,#6f42c1)",
              color: "#fff", fontWeight: 700, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: "0 4px 12px rgba(85,110,230,0.3)",
              opacity: saveLoading ? 0.8 : 1,
            }}
          >
            {saveLoading
              ? <><Spinner size="sm" />Kaydediliyor...</>
              : <><i className="bx bx-check" />{editRow ? "Güncelle" : "Kaydet"}</>}
          </button>
        </ModalFooter>
      </Modal>

      {/* ════ SİLME ONAY MODALI ════════════════════════════════ */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered size="sm">
        <ModalHeader toggle={() => setDeleteModal(false)} style={{ borderBottom: "none" }}>
          <div className="d-flex align-items-center gap-2">
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bx bx-trash text-white" style={{ fontSize: 17 }} />
            </div>
            <span style={{ fontWeight: 700 }}>Kaydı Sil</span>
          </div>
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: 14, color: "#495057", margin: 0 }}>
            Bu kaydı silmek istediğinizden emin misiniz?
            <br />
            <small className="text-danger">
              Bu işlem geri alınamaz. Kullanımda olan kayıtlar silinemeyebilir.
            </small>
          </p>
        </ModalBody>
        <ModalFooter style={{ borderTop: "none" }}>
          <button onClick={() => setDeleteModal(false)} style={{
            padding: "7px 18px", borderRadius: 8, border: "1.5px solid #dee2e6",
            background: "#fff", color: "#6c757d", fontWeight: 600, cursor: "pointer",
          }}>
            Vazgeç
          </button>
          <button onClick={handleDelete} disabled={saveLoading} style={{
            padding: "7px 18px", borderRadius: 8, border: "none",
            background: "linear-gradient(135deg,#dc3545,#ff6b6b)",
            color: "#fff", fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 5,
            opacity: saveLoading ? 0.8 : 1,
          }}>
            {saveLoading ? <><Spinner size="sm" />Siliniyor...</> : <><i className="bx bx-trash" />Evet, Sil</>}
          </button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default FiyatYonetimi;
